import { useCallback, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useBookingOperations as useBookingOps } from './BookingOperationsContext';
import { BOOKING_OPERATIONS } from './types';

/**
 * useBookingOperations - Enhanced hook for booking operations
 *
 * This hook provides a simplified API for common booking operations
 * and can be used directly in components without needing the provider.
 *
 * @example
 * // Basic usage
 * const { cancelBooking, isLoading } = useBookingOperations();
 *
 * // Cancel a booking
 * await cancelBooking(bookingId, {
 *   onSuccess: () => console.log('Booking cancelled'),
 *   onError: (error) => console.error('Error:', error)
 * });
 *
 * @example
 * // With UI state management
 * const booking = useBookingOperations({
 *   resetStates: {
 *     dialog: setIsDialogOpen,
 *     loading: setIsLoading,
 *   },
 *   clearSelections: [setSelectedItems],
 *   triggerRefresh: () => refetch()
 * });
 *
 * @example
 * // For subscription-based operations
 * const { createBookingCallback } = useBookingOperations();
 *
 * const bookingCallback = createBookingCallback({
 *   operation: BOOKING_OPERATIONS.CANCEL,
 *   resetStates: {
 *     dialog: setOpenDialog,
 *     status: setStatus
 *   }
 * });
 *
 * <BookingNotificationStatus onBookingCallback={bookingCallback} />
 */
export const useBookingOperations = (defaultConfig = {}) => {
  const context = useBookingOps();
  const user = useSelector((store) => store.user);

  // Memoize the default configuration
  // Only include stable user properties to avoid re-renders
  const config = useMemo(() => ({
    ...defaultConfig,
    user: {
      id: user?.id,
      email: user?.email,
      user: user?.user,
    },
  }), [defaultConfig, user?.id, user?.email, user?.user]);

  /**
   * Cancel booking with merged config
   */
  const cancelBooking = useCallback(async (bookingId, operationConfig = {}) => {
    return context.cancelBooking(bookingId, {
      ...config,
      ...operationConfig,
    });
  }, [context, config]);

  /**
   * Approve booking with merged config
   */
  const approveBooking = useCallback(async (bookingId, operationConfig = {}) => {
    return context.approveBooking(bookingId, {
      ...config,
      ...operationConfig,
    });
  }, [context, config]);

  /**
   * Reject booking with merged config
   */
  const rejectBooking = useCallback(async (bookingId, operationConfig = {}) => {
    return context.rejectBooking(bookingId, {
      ...config,
      ...operationConfig,
    });
  }, [context, config]);

  /**
   * Create booking callback with merged config
   */
  const createBookingCallback = useCallback((operationConfig = {}) => {
    return context.createBookingCallback({
      ...config,
      ...operationConfig,
    });
  }, [context, config]);

  /**
   * Helper to check if a specific operation is loading
   */
  const isOperationLoading = useCallback((operation) => {
    return context.getOperationStatus(operation) === 'LOADING';
  }, [context]);

  return {
    // Operations
    cancelBooking,
    approveBooking,
    rejectBooking,
    createBookingCallback,

    // Status helpers
    isLoading: context.isAnyOperationLoading(),
    isOperationLoading,
    getOperationStatus: context.getOperationStatus,

    // Constants for convenience
    OPERATIONS: BOOKING_OPERATIONS,

    // User context
    user,
  };
};

/**
 * Hook for components that only need the callback factory
 *
 * @example
 * const bookingCallback = useBookingCallback({
 *   operation: BOOKING_OPERATIONS.CANCEL,
 *   resetStates: { dialog: setIsOpen }
 * });
 */
export const useBookingCallback = (config = {}) => {
  const { createBookingCallback } = useBookingOperations();

  return useMemo(() => {
    return createBookingCallback(config);
  }, [createBookingCallback, config]);
};

/**
 * Hook for subscription status handling (backwards compatibility)
 *
 * @example
 * useBookingSubscriptionStatus(subscriptionStatus, setSubscriptionStatus);
 */
export const useBookingSubscriptionStatus = (subscriptionStatus, setSubscriptionStatus) => {
  const { createBookingCallback } = useBookingOperations();

  // Create a callback that handles the subscription status
  const callback = useMemo(() => {
    return createBookingCallback({
      operation: BOOKING_OPERATIONS.CREATE,
      skipCheckInSucceeded: false,
      onSuccess: () => {
        setSubscriptionStatus(null);
      },
      onError: () => {
        setSubscriptionStatus(null);
      }
    });
  }, [createBookingCallback, setSubscriptionStatus]);

  // Process the subscription status when it changes
  useEffect(() => {
    if (subscriptionStatus) {
      callback({
        data: {
          onUpdateBookingCommand: subscriptionStatus
        }
      });
    }
  }, [subscriptionStatus, callback]);
};

export default useBookingOperations;
